import React, { lazy } from 'react';
import { appRoutes, AppRoutesObject, ViewRestricted } from './routes.types';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthUtils } from 'src/services/Auth/Auth.utils';
import FullPageWrapper from 'src/containers/FullPageWrapper';

const WrapperLayout = lazy(() => import('src/components/ui/layouts/wrapper-layout'));
const UnauthenticatedLayout = lazy(
  () => import('src/components/ui/layouts/unauthenticated-layout'),
);
const UserAuthenticationLayout = lazy(
  () => import('src/components/ui/layouts/user-authentication-layout'),
);
const AuthenticatedLayout = lazy(
  () => import('src/components/ui/layouts/authenticated-layout/authenticated-layout'),
);
const AddSubscriptionLayout = lazy(
  () => import('src/components/ui/layouts/add-subscription-layout/add-subscription-layout'),
);
const SubscriptionCancelationLayout = lazy(
  () =>
    import(
      'src/components/ui/layouts/subscription-cancelation-layout/subscription-cancelation-layout'
    ),
);

const restricted: { [key in ViewRestricted]: ViewRestricted } = {
  guest: 'guest',
  customer: 'customer',
};

const RootPage = () => {
  const isLogged = !AuthUtils.isExpired();
  const location = useLocation();

  if (!isLogged) {
    return <Navigate to={{ pathname: appRoutes.login, search: location.search }} replace={true} />;
  }

  return <Navigate to={appRoutes.dashboard} replace={true} />;
};

const routes: AppRoutesObject = {
  root: {
    path: appRoutes.root,
    element: <RootPage />,
    layout: WrapperLayout,
    index: true,
  },
  notFound: {
    path: appRoutes.notFound,
    lazy: async () => {
      const { NotFoundPage } = await import('src/pages/NotFoundPage/NotFoundPage');
      return { Component: NotFoundPage };
    },
    layout: WrapperLayout,
  },
  maintenance: {
    path: appRoutes.maintenance,
    lazy: async () => {
      const { MaintenancePage } = await import('src/pages/maintenance-page/maintenance-page');
      return { Component: MaintenancePage };
    },
    layout: WrapperLayout,
  },
  impersonate: {
    path: appRoutes.impersonate,
    lazy: async () => {
      const { ImpersonateUserPage } = await import(
        'src/pages/ImpersonateUserPage/ImpersonateUserPage'
      );
      return { Component: ImpersonateUserPage };
    },
    layout: FullPageWrapper,
  },
  login: {
    path: appRoutes.login,
    lazy: async () => {
      const { LoginPage } = await import('src/pages/auth/login-page');
      return { Component: LoginPage };
    },
    layout: UserAuthenticationLayout,
    restricted: [restricted.customer],
  },
  register: {
    path: appRoutes.register,
    lazy: async () => {
      const { RegisterPage } = await import('src/pages/auth/register-page');
      return { Component: RegisterPage };
    },
    layout: UserAuthenticationLayout,
    restricted: [restricted.customer],
  },
  stripePaymentLinkRedirect: {
    path: appRoutes.stripePaymentLinkRedirect,
    lazy: async () => {
      const { StripePaymentLinkRedirectPage } = await import(
        'src/pages/stripe-payment-link-redirect-page'
      );
      return { Component: StripePaymentLinkRedirectPage };
    },
    layout: WrapperLayout,
    restricted: [restricted.guest],
  },
  verifyEmail: {
    path: appRoutes.verifyEmail,
    lazy: async () => {
      const { VerifyEmailPage } = await import('src/pages/verify-email/verify-email-page');
      return { Component: VerifyEmailPage };
    },
    layout: WrapperLayout,
  },
  authVerifyEmail: {
    path: appRoutes.authVerifyEmail,
    lazy: async () => {
      const { AuthVerifyEmailPage } = await import('src/pages/verify-email/auth-verify-email-page');
      return { Component: AuthVerifyEmailPage };
    },
    layout: WrapperLayout,
    restricted: [restricted.guest],
  },
  forgotPassword: {
    path: appRoutes.forgotPassword,
    lazy: async () => {
      const { ForgotPasswordPage } = await import('src/pages/auth/forgot-password-page');
      return { Component: ForgotPasswordPage };
    },
    layout: UserAuthenticationLayout,
    restricted: [restricted.customer],
  },
  forgotPasswordReset: {
    path: appRoutes.forgotPasswordReset,
    lazy: async () => {
      const { ResetPasswordPage } = await import('src/pages/auth/reset-password-page');
      return { Component: ResetPasswordPage };
    },
    layout: UserAuthenticationLayout,
    restricted: [restricted.customer],
  },
  sharedMedia: {
    path: appRoutes.sharedMedia,
    lazy: async () => {
      const { SharedRevisionMediaPage } = await import(
        'src/pages/SharedMedia/SharedRevisionMediaPage'
      );
      return { Component: SharedRevisionMediaPage };
    },
    layout: FullPageWrapper,
    restricted: [],
  },
  dashboard: {
    path: appRoutes.dashboard,
    lazy: async () => {
      const { DashboardPage } = await import('src/pages/dashboard/dashboard-page');
      return { Component: DashboardPage };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  addSubscriptionPlanSelect: {
    path: appRoutes.addSubscriptionPlanSelect,
    lazy: async () => {
      const { PlanSelectPage } = await import(
        'src/pages/subscription/add-subscription/plan-select-page'
      );
      return { Component: PlanSelectPage };
    },
    layout: AddSubscriptionLayout,
    restricted: [restricted.guest],
  },
  addSubscriptionSubscribe: {
    path: appRoutes.addSubscriptionSubscribe,
    lazy: async () => {
      const { PaymentPage } = await import('src/pages/subscription/add-subscription/payment-page');
      return { Component: PaymentPage };
    },
    layout: AddSubscriptionLayout,
    restricted: [restricted.guest],
  },
  company: {
    path: appRoutes.company,
    lazy: async () => {
      const { CompanyPage } = await import('src/pages/company/company-page');
      return { Component: CompanyPage };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  companyPersonnel: {
    path: appRoutes.companyPersonnel,
    lazy: async () => {
      const { CompanyPersonnelPage } = await import(
        'src/pages/company-personnel/company-personnel-page'
      );
      return { Component: CompanyPersonnelPage };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  requestsList: {
    path: appRoutes.requestsList,
    lazy: async () => {
      const { RequestsListPage } = await import(
        'src/pages/request/requests-list/requests-list-page'
      );
      return { Component: RequestsListPage };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  request: {
    path: appRoutes.request,
    lazy: async () => {
      const { RequestPage } = await import('src/pages/request/request-page/request-page');
      return { Component: RequestPage };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  requestRevisions: {
    path: appRoutes.requestRevisions,
    lazy: async () => {
      const { RevisionsPage } = await import('src/pages/RevisionsPage/RevisionsPage');
      return { Component: RevisionsPage };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  templatesList: {
    path: appRoutes.templatesList,
    lazy: async () => {
      const { VideoTemplatesListPage } = await import(
        'src/pages/video-templates-list/video-templates-list-page'
      );
      return { Component: VideoTemplatesListPage };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  createBrand: {
    path: appRoutes.createBrand,
    lazy: async () => {
      const { NewBrandOnboardingPage } = await import(
        'src/pages/new-brand-onboarding/NewBrandOnboardingPage'
      );
      return { Component: NewBrandOnboardingPage };
    },
    layout: FullPageWrapper,
    restricted: [restricted.guest],
  },
  brand: {
    path: appRoutes.brand,
    lazy: async () => {
      const { EditBrandPage } = await import('src/pages/brand/edit-brand-page');
      return { Component: EditBrandPage };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  brandLegacy: {
    path: appRoutes.brandLegacy,
    lazy: async () => {
      const { LegacyEditBrandPage } = await import('src/pages/brand/legacy-edit-brand-page');
      return { Component: LegacyEditBrandPage };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  brandAssistance: {
    path: appRoutes.brandAssistance,
    lazy: async () => {
      const { BrandAssistancePage } = await import('src/pages/brand/brand-assistance-page');
      return { Component: BrandAssistancePage };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  profile: {
    path: appRoutes.profile,
    lazy: async () => {
      const { ProfilePage } = await import('src/pages/profile/profile-page');
      return { Component: ProfilePage };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  notificationSettings: {
    path: appRoutes.notificationSettings,
    lazy: async () => {
      const { NotificationSettingsPage } = await import(
        'src/pages/profile/notification-settings-page'
      );
      return { Component: NotificationSettingsPage };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  accountOnHold: {
    path: appRoutes.accountOnHold,
    lazy: async () => {
      const { AccountOnHoldPage } = await import('src/pages/account-on-hold/account-on-hold-page');
      return { Component: AccountOnHoldPage };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  socialTikTokAuth: {
    path: appRoutes.socialTikTokAuth,
    lazy: async () => {
      const { TikTokAuthPage } = await import('src/pages/social/TikTok/TikTokAuthPage');
      return { Component: TikTokAuthPage };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  socialLogin: {
    path: appRoutes.socialLogin,
    lazy: async () => {
      const { LoginCallbackPage } = await import('src/pages/social/login-callback-page');
      return { Component: LoginCallbackPage };
    },
    restricted: [],
    layout: FullPageWrapper,
  },
  socialProviderCallback: {
    path: appRoutes.socialProviderCallback,
    lazy: async () => {
      const { AddSocialCallbackPage } = await import('src/pages/social/add-social-callback-page');
      return { Component: AddSocialCallbackPage };
    },
    restricted: [],
    layout: FullPageWrapper,
  },
  socialTracking: {
    path: appRoutes.socialTracking,
    lazy: async () => {
      const { SocialTrackingPage } = await import('src/pages/social/tracking');
      return { Component: SocialTrackingPage };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  socialEntityDetail: {
    path: `${appRoutes.socialTracking}/entity/:entityId`,
    lazy: async () => {
      const EntityDetailPage = await import('src/pages/social/tracking/entity-detail-page');
      return { Component: EntityDetailPage.default };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  // repurposing: {
  //   path: appRoutes.repurposing,
  //   lazy: async () => {
  //     const { RepurposingPage } = await import('src/pages/repurposing/repurposing-page');
  //     return { Component: RepurposingPage };
  //   },
  //   layout: AuthenticatedLayout,
  //   restricted: [restricted.guest],
  // },
  mediaLibrary: {
    path: appRoutes.mediaLibrary,
    lazy: async () => {
      const { MediaLibraryPage } = await import('../pages/media-library');
      return { Component: MediaLibraryPage };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  aiAgent: {
    path: appRoutes.aiAgent,
    lazy: async () => {
      const { AIAgentPage } = await import('src/pages/ai-agent');
      return { Component: AIAgentPage };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  scriptsList: {
    path: appRoutes.scriptsList,
    lazy: async () => {
      const { ScriptsListPage } = await import('src/pages/scripts/scripts-list-page');
      return { Component: ScriptsListPage };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  createScript: {
    path: appRoutes.createScript,
    lazy: async () => {
      const { ScriptEditorPage } = await import('src/pages/scripts/script-editor-page');
      return { Component: ScriptEditorPage };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  editScript: {
    path: appRoutes.editScript,
    lazy: async () => {
      const { ScriptEditorPage } = await import('src/pages/scripts/script-editor-page');
      return { Component: ScriptEditorPage };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  contentPlan: {
    path: appRoutes.contentPlan,
    lazy: async () => {
      const { IdeasPage } = await import('src/pages/content-plan/ideas-page');
      return { Component: IdeasPage };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  benIdeas: {
    path: appRoutes.benIdeas,
    lazy: async () => {
      const { MyThoughtsPage } = await import('src/pages/content-plan/my-thoughts-page');
      return { Component: MyThoughtsPage };
    },
    layout: AuthenticatedLayout,
    restricted: [restricted.guest],
  },
  subscriptionCancelation: {
    path: appRoutes.subscriptionCancelation,
    lazy: async () => {
      const { SubscriptionCancelationPage } = await import(
        'src/pages/subscription-cancelation/subscription-cancelation-page'
      );
      return { Component: SubscriptionCancelationPage };
    },
    layout: SubscriptionCancelationLayout,
    restricted: [restricted.guest, restricted.customer],
  },
  any: {
    path: appRoutes.any,
    lazy: async () => {
      const { NotFoundPage } = await import('src/pages/NotFoundPage/NotFoundPage');
      return { Component: NotFoundPage };
    },
    layout: FullPageWrapper,
  },
};

export { routes };
